import { ConfirmModal } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { useLocalName } from "@/shared/localName";
import type { OutOfStockDraft } from "@/modules/pos/lib/outOfStockDrafts";

/**
 * T11 / D173 — AN 86'D DISH AT FIRE WARNS, NEVER BLOCKS (D3).
 *
 * «نفد «{item}» بعد إضافته. هل تريد الإرسال رغم ذلك؟» — «إرسال رغم ذلك»
 * sends exactly what was about to be sent; «إلغاء» sends nothing and leaves
 * the basket as it was, so the cashier can swap the dish or ask the table.
 * One dialog for every door that fires: the till's Send, a course's «send
 * these», the crew meal, and the «بانتظار أمين الصندوق» lane's send.
 *
 * Caution tone: the act reaches past this screen (the kitchen) but moves no
 * money; nothing in it is destructive.
 */
export function OutOfStockFireConfirm({
  items,
  onSend,
  onCancel,
  isSubmitting = false,
}: {
  /** The 86'd dishes about to be sent; null keeps the dialog closed. */
  items: readonly OutOfStockDraft[] | null;
  onSend: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const localName = useLocalName();
  const names = (items ?? []).map((item) =>
    localName({ nameAr: item.nameAr, nameEn: item.nameEn }),
  );
  return (
    <ConfirmModal
      isOpen={items !== null && items.length > 0}
      onClose={onCancel}
      onConfirm={onSend}
      tone="caution"
      title={t((m) => m.pos.outAtFireTitle)}
      description={
        <span data-testid="out-at-fire-body">
          {t((m) => m.pos.outAtFireBody, { item: names.join(locale === "ar" ? "، " : ", ") })}
        </span>
      }
      confirmLabel={t((m) => m.pos.outAtFireSend)}
      cancelLabel={t((m) => m.pos.outAtFireCancel)}
      closeLabel={t((m) => m.common.close)}
      isSubmitting={isSubmitting}
      testId="out-at-fire"
    />
  );
}
